/* Back-to-school assistant: guided checklist with links to setup pages.
 * Reads /api/back-to-school/checklist?school_year=...
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { sectionsApi } from '../../lib/api';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  CheckCircle, Warning, XCircle, ArrowRight, ArrowClockwise,
  Backpack, EnvelopeSimple,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_ICON = {
  ok: <CheckCircle size={20} weight="fill" className="text-emerald-500" />,
  warn: <Warning size={20} weight="fill" className="text-amber-500" />,
  error: <XCircle size={20} weight="fill" className="text-red-500" />,
};

const STATUS_ROW_CLS = {
  ok: 'border-emerald-100 bg-emerald-50/30',
  warn: 'border-amber-100 bg-amber-50/40',
  error: 'border-red-100 bg-red-50/40',
};

const ChecklistItem = ({ item }) => (
  <div
    className={`flex items-start gap-3 p-3 rounded-lg border ${STATUS_ROW_CLS[item.status] || 'border-[#E2E8F0]'}`}
    data-testid={`b2s-item-${item.id}`}
    data-status={item.status}
  >
    <div className="mt-0.5">{STATUS_ICON[item.status]}</div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="font-medium text-[#0F172A] text-sm">{item.label}</p>
        {item.total != null && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-[#E2E8F0] text-[#64748B]">
            {item.count ?? 0}/{item.total}
          </span>
        )}
      </div>
      <p className="text-xs text-[#475569] mt-0.5">{item.detail}</p>
    </div>
    {item.action_url && item.status !== 'ok' && (
      <Link to={item.action_url} className="shrink-0">
        <Button
          variant="outline" size="sm"
          className="text-xs"
          data-testid={`b2s-action-${item.id}`}
        >
          Corriger <ArrowRight size={12} className="ml-1" />
        </Button>
      </Link>
    )}
  </div>
);

const ProgressBar = ({ pct }) => (
  <div className="h-2 bg-[#E2E8F0] rounded-full overflow-hidden">
    <div
      className={`h-full transition-all ${
        pct === 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-[#0052FF]' : 'bg-amber-500'
      }`}
      style={{ width: `${pct}%` }}
    />
  </div>
);

const BackToSchoolAssistant = ({ schoolYear }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!schoolYear) return;
    setLoading(true);
    try {
      const { data: d } = await sectionsApi.backToSchoolChecklist(schoolYear);
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement assistant');
    } finally { setLoading(false); }
  }, [schoolYear]);

  const sendReminderNow = async () => {
    if (!window.confirm("Envoyer l'email de rappel maintenant à tous les administrateurs et directeurs des études ?")) return;
    try {
      const { data: d } = await api.post('/back-to-school/send-reminder');
      if (d.sent_count > 0) {
        toast.success(`Email envoyé à ${d.sent_count} destinataire(s).`);
      } else {
        toast.warning(`Aucun email envoyé (${d.errors || 0} erreur(s)). Vérifiez la config Resend.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi email');
    }
  };

  useEffect(() => { load(); }, [load]);

  if (!data && !loading) return null;

  const summary = data?.summary || {};
  const pct = summary.completion_pct ?? 0;

  return (
    <Card className="border border-[#E2E8F0] shadow-none" data-testid="back-to-school-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-outfit text-base flex items-center gap-2">
          <Backpack size={20} className="text-[#0052FF]" weight="duotone" />
          Assistant de rentrée — {schoolYear}
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={sendReminderNow}
            className="border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF]/5"
            data-testid="b2s-send-reminder-btn"
          >
            <EnvelopeSimple size={14} className="mr-1" weight="duotone" /> Envoyer l&apos;email
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={load} disabled={loading}
            data-testid="b2s-refresh-btn"
          >
            <ArrowClockwise size={14} className="mr-1" /> {loading ? '…' : 'Actualiser'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress + summary */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-baseline gap-2">
              <p
                className={`font-outfit text-3xl font-bold ${
                  pct === 100 ? 'text-emerald-600' : pct >= 60 ? 'text-[#0052FF]' : 'text-amber-600'
                }`}
                data-testid="b2s-completion-pct"
              >
                {pct}%
              </p>
              <p className="text-xs text-[#64748B]">
                {summary.ok}/{summary.total} items validés
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1 text-emerald-700" data-testid="b2s-ok-count">
                <CheckCircle size={12} weight="fill" /> {summary.ok ?? 0} OK
              </span>
              <span className="flex items-center gap-1 text-amber-700" data-testid="b2s-warn-count">
                <Warning size={12} weight="fill" /> {summary.warn ?? 0} À vérifier
              </span>
              <span className="flex items-center gap-1 text-red-700" data-testid="b2s-error-count">
                <XCircle size={12} weight="fill" /> {summary.error ?? 0} Bloquant
              </span>
            </div>
          </div>
          <ProgressBar pct={pct} />
          {summary.ready_for_school_start ? (
            <p className="text-xs text-emerald-700 font-medium">
              ✅ Aucun bloqueur : l&apos;école est prête à démarrer.
            </p>
          ) : (
            <p className="text-xs text-amber-700">
              Il reste des points à traiter avant la rentrée. Cliquez sur « Corriger » pour ouvrir la page concernée.
            </p>
          )}
        </div>

        {/* Items list */}
        <div className="space-y-2" data-testid="b2s-items">
          {(data?.items || []).map((it) => (
            <ChecklistItem key={it.id} item={it} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default BackToSchoolAssistant;
